import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";

// Each letter turns through its edge. v/i land as A/I, r as +, pluz as PLUS.
const GLYPHS = [
  { from: "v", to: "A", meaning: true },
  { from: "i", to: "I", meaning: true },
  { from: "r", to: "+", meaning: true },
  { from: "p", to: "P" },
  { from: "l", to: "L" },
  { from: "u", to: "U" },
  { from: "z", to: "S" },
];

const STORIES = {
  full: [
    { indices: [0, 1], after: 180 },
    { indices: [2], after: 120 },
    { indices: [3, 4, 5, 6], after: 1680 },
  ],
};

const CLOSE_EASE = "cubic-bezier(0.55, 0, 1, 0.45)";
const OPEN_EASE = "cubic-bezier(0.16, 1, 0.3, 1)";
const SEEN_KEY = "virpluz-wordmark-seen";

const resting = () => GLYPHS.map((glyph) => ({ char: glyph.from, meaning: false }));

function glyphState(index, side) {
  const glyph = GLYPHS[index];
  if (side === "to") return { char: glyph.to, meaning: Boolean(glyph.meaning) };
  return { char: glyph.from, meaning: false };
}

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function canAnimate() {
  return typeof HTMLElement !== "undefined" && typeof HTMLElement.prototype.animate === "function" && !prefersReducedMotion();
}

function WordmarkFlip({ story = "full", play = "once", active = true }) {
  const [chars, setChars] = useState(resting);
  const [live, setLive] = useState(false);
  const rootRef = useRef(null);
  const letterRefs = useRef([]);
  const rotorRefs = useRef([]);
  const runId = useRef(0);
  const timers = useRef([]);

  const clearTimers = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  };

  const wait = (ms) =>
    new Promise((resolve) => {
      timers.current.push(setTimeout(resolve, ms));
    });

  const cancelAnimations = () => {
    letterRefs.current.forEach((el) => el?.getAnimations?.().forEach((anim) => anim.cancel()));
    rotorRefs.current.forEach((el) => {
      if (!el) return;
      el.getAnimations?.().forEach((anim) => anim.cancel());
      el.style.transform = "";
    });
  };

  const restore = () => {
    cancelAnimations();
    setChars(resting());
    setLive(false);
  };

  const actions = useRef({});

  const afterAnimation = (animation, duration) =>
    new Promise((resolve) => {
      let settled = false;
      const done = () => {
        if (settled) return;
        settled = true;
        resolve();
      };
      timers.current.push(setTimeout(done, duration + 60));
      animation.finished.then(done, done);
    });

  const closeRotor = (index, duration) => {
    const rotor = rotorRefs.current[index];
    if (!rotor) return Promise.resolve();
    rotor.getAnimations().forEach((anim) => anim.cancel());
    rotor.style.transform = "";
    const animation = rotor.animate([{ transform: "rotateX(0deg)" }, { transform: "rotateX(90deg)" }], {
      duration,
      easing: CLOSE_EASE,
      fill: "forwards",
    });
    return afterAnimation(animation, duration);
  };

  const openRotor = (index, duration) => {
    const rotor = rotorRefs.current[index];
    if (!rotor) return Promise.resolve();
    const animation = rotor.animate([{ transform: "rotateX(-90deg)" }, { transform: "rotateX(0deg)" }], {
      duration,
      easing: OPEN_EASE,
      fill: "forwards",
    });
    rotor.style.transform = "";
    return afterAnimation(animation, duration).then(() => {
      if (animation.playState !== "idle") animation.cancel();
      rotor.style.transform = "";
    });
  };

  const runBeat = async (indices, side, id) => {
    const close = side === "to" ? 210 : 160;
    const open = side === "to" ? 360 : 270;
    await Promise.all(indices.map((index) => closeRotor(index, close)));
    if (id !== runId.current) return;

    const letters = letterRefs.current;
    const before = letters.map((el) => el?.offsetLeft ?? 0);
    indices.forEach((index) => {
      const rotor = rotorRefs.current[index];
      if (!rotor) return;
      rotor.getAnimations().forEach((anim) => anim.cancel());
      rotor.style.transform = "rotateX(-90deg)";
    });
    flushSync(() => {
      setChars((prev) => {
        const next = prev.slice();
        indices.forEach((index) => {
          next[index] = glyphState(index, side);
        });
        return next;
      });
    });
    if (id !== runId.current) return;

    const slides = letters.map((el, index) => {
      if (!el || indices.includes(index)) return null;
      const dx = before[index] - el.offsetLeft;
      if (Math.abs(dx) < 0.5) return null;
      const animation = el.animate(
        [{ transform: `translateX(${dx}px)` }, { transform: "translateX(0px)" }],
        { duration: open, easing: OPEN_EASE }
      );
      return afterAnimation(animation, open);
    });

    await Promise.all([
      ...indices.map((index) => openRotor(index, open)),
      ...slides.filter(Boolean),
    ]);
  };

  const start = () => {
    if (!canAnimate()) return;
    const id = ++runId.current;
    clearTimers();
    cancelAnimations();
    flushSync(() => {
      setChars(resting());
      setLive(true);
    });

    const playStory = async () => {
      const beats = STORIES[story];
      for (const beat of beats) {
        if (id !== runId.current) return;
        await runBeat(beat.indices, "to", id);
        if (id !== runId.current) return;
        await wait(beat.after);
      }
      for (const beat of [...beats].reverse()) {
        if (id !== runId.current) return;
        await runBeat(beat.indices, "from", id);
        if (id !== runId.current) return;
        await wait(70);
      }
      if (id === runId.current) setLive(false);
    };

    playStory();
  };

  actions.current = { start, restore };

  useEffect(() => {
    if (play !== "once") return undefined;
    let seen = false;
    try {
      seen = sessionStorage.getItem(SEEN_KEY) === "1";
    } catch {
      seen = false;
    }
    if (seen) return undefined;
    const timer = setTimeout(() => {
      try {
        sessionStorage.setItem(SEEN_KEY, "1");
      } catch {
        // Session storage can be blocked. The mark still plays this once.
      }
      actions.current.start();
    }, 700);
    return () => clearTimeout(timer);
  }, [play]);

  useEffect(() => {
    if (play !== "when-active") return undefined;
    if (!active) {
      runId.current += 1;
      clearTimers();
      actions.current.restore();
      return undefined;
    }
    const timer = setTimeout(() => actions.current.start(), 220);
    return () => clearTimeout(timer);
  }, [play, active]);

  useEffect(() => {
    if (play !== "inview") return undefined;
    const node = rootRef.current;
    if (!node || !canAnimate()) return undefined;
    let started = false;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || started) return;
        started = true;
        observer.disconnect();
        actions.current.start();
      },
      { threshold: 0.35 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [play]);

  useEffect(() => {
    const node = rootRef.current;
    if (!node) return undefined;
    const target = node.closest("a, .footer-word") ?? node;
    const onEnter = () => actions.current.start();
    target.addEventListener("mouseenter", onEnter);
    return () => target.removeEventListener("mouseenter", onEnter);
  }, []);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => {
      if (!media.matches) return;
      runId.current += 1;
      clearTimers();
      actions.current.restore();
    };
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  useEffect(
    () => () => {
      runId.current += 1;
      clearTimers();
    },
    []
  );

  const className = `wordmark-flip${story === "full" ? " is-display" : ""}${live ? " is-live" : ""}`;

  return (
    <span ref={rootRef} className={className} aria-hidden="true">
      {chars.map((glyph, index) => (
        <span
          className="flip-letter"
          key={GLYPHS[index].from}
          ref={(node) => {
            letterRefs.current[index] = node;
          }}
        >
          <span
            className="flip-rotor"
            ref={(node) => {
              rotorRefs.current[index] = node;
            }}
          >
            <span
              className={`flip-char${glyph.meaning ? " is-meaning" : ""}${glyph.char === "+" ? " is-plus" : ""}`}
            >
              {glyph.char}
            </span>
          </span>
        </span>
      ))}
    </span>
  );
}

export default WordmarkFlip;
